<template>
  <Page v-if="loading">Loading...</Page>
  <main v-else class="login-page">
    <section class="login-panel">
      <div class="login-panel-content">
        <img :src="clientLogoURL" class="portal-logo" :alt="clientName">
        <h1 class="portal-client-name">{{ clientName }}</h1>
        <p class="login-intro">Login to respond to an invitation or download bid documents and plans.</p>

        <div v-if="sessionExpired" class="login-error" role="alert">
          <strong>Your session has expired</strong>
          <span>Please sign in again to continue where you left off.</span>
        </div>
        <div v-if="loginError" class="login-error" role="alert">
          <strong>Unable to sign in</strong>
          <span>{{ loginError }}</span>
        </div>

        <form class="login-form" method="post" :action="loginEndpoint" @submit="clearProjectLoginContext">
          <h1>Subcontractor Login</h1>
          <input type="hidden" name="subdomain" :value="subdomain">
          <label for="portalProjectID">Project ID</label>
          <input id="portalProjectID" v-model="portalProjectId" name="portalProjectID" type="text" placeholder="Project ID" required>
          <label for="portalSecurityKey">Security Key</label>
          <input id="portalSecurityKey" name="portalSecurityKey" type="text" placeholder="Security Key" required>
          <div class="login-actions"><button class="button is-success" type="submit">Login</button></div>
        </form>

        <p class="support-message">For support email <a href="mailto:support@preconsuite.com">support@preconsuite.com</a> or call <a href="tel:1-949-222-0400">1-949-222-0400</a></p>
      </div>
    </section>
    <aside class="brand-panel">
      <img src="https://assets.p-suite.io/preconsuite-logo.svg" alt="PreconSuite">
    </aside>
  </main>
</template>

<script setup lang="ts">

  import { computed, onBeforeMount, ref } from "vue";
  import {storeToRefs} from 'pinia';
  import { useRouter } from "vue-router";

  import Page from "@/components/Page.vue";
  import Auth from "@/auth";
  import LoginContextAPI from '@/api/LoginContextAPI';
  import {useClientStore} from '@/stores/ClientStore';
  import clientSubdomain from '@/use/clientSubdomain';
  import {
    findRouteByName,
  } from "@/internal";

  const loading = ref(true);
  const {client} = storeToRefs(useClientStore());
  const portalProjectId = ref('');
  const loginEndpoint = `${import.meta.env.VITE_APP_API_URL}/auth/login`;
  const subdomain = clientSubdomain() || '';
  const clientName = computed(() => client.value?.name || 'SubsPortal');
  const clientLogoURL = computed(() => client.value?.logo_url || 'https://opr.pipelinesuite.com/2/ps_icon_dark1.png');
  const urlParams = new URLSearchParams(window.location.search);
  const sessionExpired = computed(() => urlParams.get('reason') === 'session_expired');
  const loginError = ref(urlParams.get('login_error'));
  const loginContextAuthKey = 'login_context_auth';

  const clearProjectLoginContext = () => {
    window.sessionStorage.removeItem('project_id');
  };

  onBeforeMount(async () => {
    const loginContext = urlParams.get('login_context');
    const emailResponse = urlParams.get('email_response');
    const authenticated = Auth.isAuthenticated();
    portalProjectId.value = urlParams.get('portal_project_id') || '';

    if (!authenticated || loginError.value) {
      clearProjectLoginContext();
    }

    if (loginError.value) {
      loading.value = false;
      return;
    }

    if (authenticated) {
      const router = useRouter();

      if (loginContext) {
        if (window.sessionStorage.getItem(loginContextAuthKey) !== loginContext) {
          Auth.clearSession();
          window.sessionStorage.setItem(loginContextAuthKey, loginContext);
          Auth.authenticate(undefined, window.location.href);
          return;
        }

        try {
          const context = await new LoginContextAPI().consume(loginContext);
          const projectId = String(context.project_id);

          window.sessionStorage.setItem('project_id', projectId);
          window.sessionStorage.removeItem(loginContextAuthKey);

          if (emailResponse) {
            window.sessionStorage.setItem('pending_email_response', JSON.stringify({
              confirmed: emailResponse,
              project_id: context.project_id,
              company_id: context.company_id,
            }));
          }

          const route = findRouteByName('projects/show');
          route.params = {
            ...route.params,
            projectId,
          };

          router.push(route);
        } catch (error) {
          clearProjectLoginContext();
          window.sessionStorage.removeItem(loginContextAuthKey);
          loginError.value = 'Your verified project login has expired. Please sign in again.';
          loading.value = false;
        }
      }
      else {
        router.push({
          name: 'projects'
        });
      }
    } else {
      if (loginContext) {
        window.sessionStorage.setItem(loginContextAuthKey, loginContext);
        Auth.authenticate(
          undefined,
          window.location.href,
        );
        return;
      }

      loading.value = false;
    }
  });

</script>

<style scoped lang="scss">
.login-page { display: grid; grid-template-columns: minmax(0, 1fr) minmax(25rem, 1fr); min-height: 100vh; }
.login-panel { align-items: center; display: flex; justify-content: center; padding: 3rem 2rem; }
.login-panel-content { max-width: 35rem; width: 100%; }
.portal-logo { display: block; margin: 0 auto 2rem; max-height: 13rem; max-width: 12rem; }
.portal-client-name { font-size: 1.65rem; font-weight: 600; margin: -1rem 0 2rem; text-align: center; }
.login-intro, .support-message { background: $white; border-left: 4px solid $success; margin-bottom: 1.5rem; padding: 1.25rem 1.5rem; }
.support-message { border-left-color: $warning; font-size: .875rem; margin: 1.5rem 0 0; }
.login-form { background: $white; border: 1px solid $grey-lighter; border-radius: 6px; box-shadow: 0 2px 3px rgba($black, .1); display: grid; gap: .75rem; padding: 1.75rem; }
.login-form h1 { font-size: 1.65rem; font-weight: 600; margin-bottom: .75rem; }
.login-form label { font-weight: 700; }
.login-form input[type='text'] { border: 1px solid $grey-lighter; border-radius: 4px; font-size: 1rem; padding: .65rem .75rem; }
.login-actions { display: flex; justify-content: flex-end; margin-top: .5rem; }
.login-error { background: #fff1f2; border-left: 4px solid $danger; color: #7f1d1d; display: grid; gap: .25rem; margin-bottom: 1rem; padding: .75rem 1rem; }
.brand-panel { align-items: center; background: #f0f0f0; border-left: 1px solid $grey-lighter; display: flex; justify-content: center; padding: 3rem 2rem; }
.brand-panel img { max-width: 34rem; width: 100%; }
@media (max-width: 767px) { .login-page { display: block; } .login-panel { min-height: 100vh; padding: 2rem 1rem; } .brand-panel { display: none; } }
</style>
